<script setup lang="ts">
import type { PdfTemplateId } from '~/constants/cvTemplates'
import type { CVData } from '~/types'
import { CV_CHAT_MAX_ANSWER, CV_CHAT_PASTED_CV_CHARS, type CvChatStorage } from '~/composables/useCvChat'
import type { TailorSummary } from '~/utils/cvTailor'
import type { CornerDownload } from '~/composables/useCvOffers'
import { rememberGuestCvTemplate } from '~/composables/useTailorEntry'
import type { ChatResultVariant } from '~/constants/chatResultExperiment'

/**
 * Chat CV builder: instead of pasting a CV, the visitor answers the AI's
 * questions one at a time, and the AI turns the interview into the best CV it
 * can honestly write from it. The first message asks whether the visitor
 * already has a CV or a LinkedIn profile, answered by tapping yes or no like
 * any quick reply, or in the answer box, which is there from the start: with
 * one, they attach it (PDF, Word, TXT) with the paperclip in the answer box or
 * paste its text, and the coach asks whether to add anything (a new job, a
 * course) before the CV is built; without one, the interview starts from the
 * name.
 * A visitor who pastes their CV into the chat anyway is offered the build
 * right away too.
 *
 * While the CV is being built, a dialog asks for the job posting it is for
 * (the text or a link), or lets the visitor skip it: the wait is used, and
 * the CV is built without the posting either way, and the result is not
 * tailored to it either. The result shows the CV first; next to it on
 * desktop, at the top of the offers, the tailoring card with the posting in
 * it, saying the CV can be tailored to it and what that costs (the visitor
 * runs it, the free preview first). On phones a bubble over the CV says the
 * same in one line and takes them there. Skipped, the same place waits for a
 * posting, and the bubble asks for one.
 *
 * It ends on the same templates and offer column as the paste generator:
 * Basic preselected and free to download clean, the other layouts watermarked
 * for free or sold as one clean PDF, and the Job Search Pack to keep editing.
 *
 * On the homepage this is the hero (funnel events via useHeroExperiment);
 * `withHeading` renders its headline. In the app (/cv/chat) the interview is
 * kept on a draft CV instead (`storage`, `cvId`, see useCvChatDraft).
 */
const props = withDefaults(defineProps<{
  /** Label on the saved interview and the guest CV, so the admin can tell surfaces apart. */
  source?: string
  /** A/B variant, stored with the interview. */
  variant?: string
  /** Render the hero headline above the chat (homepage). */
  withHeading?: boolean
  /** Funnel events for the A/B report. */
  onEvent?: (name: string, data?: Record<string, unknown>) => void
  /** Where the interview is kept; this browser's localStorage when not given. */
  storage?: CvChatStorage
  /** The account CV the result already is; saving it updates that CV. */
  cvId?: string | null
  /** Ask for the job posting while the CV is built; off where the posting is already known (the Civil Service tool). */
  askPosting?: boolean
  /** The template strip under the chat; off where the CV is only an input (the Civil Service tool). */
  withTemplates?: boolean
}>(), {
  source: 'ai-chat',
  variant: undefined,
  withHeading: true,
  onEvent: undefined,
  storage: undefined,
  cvId: null,
  askPosting: true,
  withTemplates: true,
})

const emit = defineEmits<{
  /** A CV was built from the interview (again, after "edit answers"). */
  built: [cv: CVData, generationId: string | null]
}>()

const { t, tm, rt } = useI18n()
const { isPremium } = usePremiumStatus()
const { isAuthenticated } = useAuthUser()

/** Past the free builds: visitors make the account first (the interview waits in this browser), members go straight to Premium. */
const upgradeTarget = computed(() => (isAuthenticated.value ? '/admin/premium' : `/registration?from=${props.source}`))

const {
  messages,
  input,
  remaining,
  done,
  suggestions,
  multipleSuggestions,
  isThinking,
  isBuilding,
  error,
  limitReached,
  limitFairUse,
  buildLimitReached,
  generatedCV,
  generationId,
  posting,
  answersLeft,
  hasStarted,
  opening,
  chooseOpening,
  answerOpening,
  importCv,
  asksAddAfterImport,
  canBuild,
  canSend,
  send,
  retry,
  answerPosting,
  build,
  backToChat,
  reset,
} = useCvChat({
  source: props.source,
  variant: props.variant,
  storage: props.storage,
  onEvent: (name, data) => props.onEvent?.(name, data),
})

/**
 * The CV always opens in Basic, which downloads clean for free; the other
 * layouts are a tap away in the picker. The offers below the preview switch
 * to match whatever is picked, and a rebuild after "edit answers" keeps it.
 */
const selectedTemplate = ref<PdfTemplateId>('basic')
watch(selectedTemplate, template => rememberGuestCvTemplate(generationId.value, template))

const outputs = [
  { id: 'pdf', icon: 'bi-file-earmark-pdf' },
  { id: 'web', icon: 'bi-globe2' },
] as const
const previewOutput = ref<(typeof outputs)[number]['id']>('pdf')

const listRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLTextAreaElement | null>(null)
const resultRef = ref<HTMLElement | null>(null)
const rootRef = ref<HTMLElement | null>(null)
/** The headline and the chat; on phones one screen-high column under the navbar. */
const columnRef = ref<HTMLElement | null>(null)
/** The messages and the answer box, pinned above the phone keyboard while typing. */
const chatRef = ref<HTMLElement | null>(null)
const offersRef = ref<HTMLElement | null>(null)
/** The offers column: the download in the preview's corner runs through it. */
const resultOffers = ref<CornerDownload | null>(null)

function scrollToOffers() {
  offersRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/**
 * Phones: straight to tailoring the CV to a posting, the answer box ready for it.
 * The field takes the focus, so the keyboard comes up over the lower half of
 * the screen: the page jumps (iOS cuts a smooth scroll short when the keyboard
 * opens) to put the card's top right under the navbar: its title says what the
 * field is for, and the field under it stays in view above the keyboard.
 * Should the browser still scroll as the keyboard comes up, the card is put
 * back once it is up.
 */
function scrollToTailor() {
  const card = document.getElementById('cv-tailor')
  if (!card) return scrollToOffers()
  // The field is still behind the card's button when the posting was skipped.
  resultOffers.value?.openTailor()
  nextTick(() => {
    const field = card.querySelector('textarea')
    if (!field) return card.scrollIntoView({ behavior: 'smooth', block: 'start' })
    const place = () => {
      const navbar = document.querySelector<HTMLElement>('.sofax-header')?.offsetHeight ?? 0
      window.scrollTo({ top: window.scrollY + card.getBoundingClientRect().top - navbar - 12, behavior: 'instant' })
    }
    place()
    const viewport = window.visualViewport
    if (!viewport) return
    const replace = () => document.activeElement === field && place()
    viewport.addEventListener('resize', replace, { once: true })
    setTimeout(() => viewport.removeEventListener('resize', replace), 1500)
  })
}

/** A complete CV never needs fewer answers than the minimum for building one. */
const completeLeft = computed(() => Math.max(remaining.value, answersLeft.value))
const lastIsUnanswered = computed(() => messages.value[messages.value.length - 1]?.role === 'user' && !isThinking.value)

/** Keeps the newest message in view; scrolls the list only, never the page. */
function scrollToBottom() {
  nextTick(() => {
    const list = listRef.value
    if (list) list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' })
  })
}

watch(() => [messages.value.length, isThinking.value, suggestions.value.length], scrollToBottom)

/** Phones would open the keyboard over the conversation, so only larger screens get the focus. */
const focusesInput = () => window.matchMedia('(min-width: 768px)').matches

/**
 * Phones: the keyboard covers half the screen and the browser scrolls the page
 * to the answer box on its own (iOS Safari whatever the page does), pushing
 * the conversation out of view above it. While the answer box has the focus,
 * the chat (messages and answer box) is pinned over exactly the visible part
 * of the screen instead, above the keyboard, the newest message right above
 * the answer box, like a messenger. It follows the visible area
 * (`visualViewport`), so however the page behind it is scrolled does not
 * matter. The hero stage isolates its stacking, so the navbar would stay on
 * top of it: it is hidden meanwhile (`chat-typing` on <html>, see sofax.scss).
 * Its background reaches the bottom of the screen: the iOS keyboard has
 * see-through corners, and the page under it would show through.
 * `null`: the chat in its place on the page.
 */
const pinned = ref<{ top: number, height: number, below: number } | null>(null)
/** Room between the answer box's last line and the keyboard. */
const KEYBOARD_GAP = 8

function pinAboveKeyboard() {
  const viewport = window.visualViewport
  if (!viewport || focusesInput() || document.activeElement !== inputRef.value) {
    pinned.value = null
    return
  }
  const height = Math.round(viewport.height)
  const resized = pinned.value?.height !== height
  const top = Math.round(viewport.offsetTop)
  // What the keyboard covers: the chat ends above it, its background goes on underneath
  const below = Math.max(0, document.documentElement.clientHeight - top - height)
  pinned.value = { top, height, below }
  // The keyboard took the room from the bottom: the newest message back above the answer box
  if (resized) nextTick(() => listRef.value && (listRef.value.scrollTop = listRef.value.scrollHeight))
}

/**
 * Phones: the keyboard closed, or the answer box was left some other way. The
 * chat goes back to its place on the page, but the page stays wherever the
 * browser scrolled it for the keyboard, often off the conversation. Once the
 * keyboard is down (the visible area grows back), the chat is brought back
 * under the navbar, unless the messages and the answer box are in view anyway.
 */
function unpin() {
  if (!pinned.value) return
  pinned.value = null
  const viewport = window.visualViewport
  const restore = () => {
    clearTimeout(timer)
    viewport?.removeEventListener('resize', restore)
    nextTick(bringChatBack)
  }
  viewport?.addEventListener('resize', restore, { once: true })
  const timer = setTimeout(restore, 600)
}

function bringChatBack() {
  const column = columnRef.value
  const list = listRef.value
  const chat = chatRef.value
  if (!column || !list || !chat || document.activeElement === inputRef.value) return
  const navbar = document.querySelector<HTMLElement>('.sofax-header')?.offsetHeight ?? 0
  if (list.getBoundingClientRect().top >= navbar && chat.getBoundingClientRect().bottom <= window.innerHeight) return
  window.scrollTo({ top: window.scrollY + column.getBoundingClientRect().top - navbar - 12, behavior: 'smooth' })
}

watch(() => !!pinned.value, (on) => {
  document.documentElement.classList.toggle('chat-typing', on)
})

/**
 * Quick replies that can be combined are ticked instead of sent: they go out
 * together with whatever was typed, as one answer. New ones start unticked.
 */
const picked = ref<string[]>([])
watch(suggestions, () => {
  picked.value = []
})

function togglePick(suggestion: string) {
  picked.value = picked.value.includes(suggestion)
    ? picked.value.filter(item => item !== suggestion)
    : [...picked.value, suggestion]
}

const canSubmit = computed(() =>
  canSend.value || (picked.value.length > 0 && !isThinking.value && !isBuilding.value && !done.value && !limitReached.value),
)

/**
 * A one-tap quick reply passes its text; otherwise the ticked replies and the
 * typed answer go out together.
 */
async function submit(text?: string) {
  const answer = text ?? [...picked.value, input.value.trim()].filter(Boolean).join(', ')
  if (awaitsImport.value || (asksOpening.value && answer.trim().length >= CV_CHAT_PASTED_CV_CHARS)) await submitImport(answer, 'paste')
  else if (asksOpening.value) await answerOpening(answer)
  else await send(answer)
  if (focusesInput()) inputRef.value?.focus()
}

/**
 * Answering is the one thing to do here, so the cursor waits in the answer box
 * on arrival. Not when the visitor already clicked into something else or came
 * for another part of the page (`/#pricing`); `preventScroll` keeps the page put.
 */
function focusOnArrival() {
  if (!focusesInput() || location.hash) return
  if (document.activeElement && document.activeElement !== document.body) return
  inputRef.value?.focus({ preventScroll: true })
}

/** Enter sends, Shift+Enter is a new line; IME composition is left alone. */
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    event.preventDefault()
    submit()
  }
}

/**
 * The build dialog: while the CV is being built, "meanwhile, which job is it
 * for?" (usePostingDialog). Asked once; a rebuild after "edit answers" keeps
 * the answer, and the result opens with the tailoring offered for it.
 */
function showResult() {
  // The CV first; the match with the posting is next to it (desktop) or in the bubble over it (phones).
  nextTick(() => resultRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}

const postingDialog = reactive(usePostingDialog({
  isBuilding,
  accept: answerPosting,
  onDone: showResult,
}))

async function buildCv() {
  if (!canBuild.value) return
  const asks = props.askPosting && posting.value === null
  if (asks) postingDialog.startBuild()
  await build()
  if (generatedCV.value) emit('built', generatedCV.value, generationId.value)
  postingDialog.endBuild(!!generatedCV.value, asks && posting.value === null)
}

/** The tailoring under the result, as the bubble over the CV shows it on phones. */
const tailorSummary = ref<TailorSummary | null>(null)
watch(generatedCV, (cv) => {
  if (!cv) tailorSummary.value = null
})

/*
 * The result is an experiment (useChatResultExperiment): A the column of
 * offers as it was, B the path and numbered steps, C one step at a time.
 * The variant is the visitor's, picked in the browser before any CV exists.
 */
const RESULT_LAYOUT = { a: 'classic', b: 'path', c: 'staged' } as const
const resultExperiment = useChatResultExperiment()
const resultVariant = ref<ChatResultVariant>('a')
const resultTracking = ref<Record<string, unknown> | undefined>(undefined)
/** Each CV counts once, however often its result is shown again. */
const exposed = new Set<string>()
watch(generatedCV, (cv) => {
  if (!cv) return
  const key = generationId.value ?? `cv:${cv.name}:${cv.title}`
  if (exposed.has(key)) return
  exposed.add(key)
  resultExperiment.trackExposure({ generationId: generationId.value, source: props.source })
})

/** The CV's headline is the role the chat asked about; one short line, or nothing. */
const role = computed(() => {
  const title = generatedCV.value?.title?.trim()
  return title && title.length <= 60 ? title : null
})

/** B and C under the title: the role when there is one; C with a posting says what waits next to the CV. */
const resultSubtitle = computed(() => {
  if (resultVariant.value === 'c' && posting.value) return t('cvChat.result.subtitlePosting')
  return role.value ? t('cvChat.result.subtitleRole', { role: role.value }) : t('cvChat.result.subtitlePath')
})

/*
 * The path over the result: the CV is done, and the three steps that turn it
 * into interviews follow, each where its offer is in the column (ResultOffers).
 * The first one not done is the next. A step is done only once it really is:
 * a plan has the web CV and every template; a tailoring seen but not taken
 * stays the next step, with its score as the reason to take it.
 */
interface PathStep {
  id: 'cv' | 'tailor' | 'interview' | 'online'
  status: 'done' | 'next' | 'todo'
  detail: string
  /** The card in the offers column it scrolls to. */
  target: string | null
}

const path = computed<PathStep[]>(() => {
  const summary = tailorSummary.value
  const scored = summary && !summary.running && summary.matchScore !== null && summary.matchScoreAfter !== null
  const steps: Omit<PathStep, 'status'>[] = [
    { id: 'cv', detail: t('cvChat.result.path.steps.cv.detail'), target: null },
    {
      id: 'tailor',
      detail: scored
        ? t('cvChat.result.path.steps.tailor.score', { before: summary.matchScore, after: summary.matchScoreAfter })
        : t('cvChat.result.path.steps.tailor.detail'),
      target: 'cv-tailor',
    },
    { id: 'interview', detail: t(isPremium.value ? 'cvChat.result.path.included' : 'cvChat.result.path.steps.interview.detail'), target: isPremium.value ? null : 'cv-interview' },
    { id: 'online', detail: t(isPremium.value ? 'cvChat.result.path.included' : 'cvChat.result.path.steps.online.detail'), target: isPremium.value ? null : 'cv-pack' },
  ]
  const done = (id: PathStep['id']) => id === 'cv' || (isPremium.value && (id === 'interview' || id === 'online'))
  const next = steps.find(step => !done(step.id))?.id
  return steps.map(step => ({ ...step, status: done(step.id) ? 'done' : step.id === next ? 'next' : 'todo' }))
})
const pathProgress = computed(() => Math.round(100 * path.value.filter(step => step.status === 'done').length / path.value.length))

/** A step's card in the column; the tailoring opens its posting field on the way. */
function goToStep(target: string) {
  journey.track('cv_result_path', { generation_id: generationId.value ?? null, step: target })
  if (target === 'cv-tailor') return scrollToTailor()
  const card = document.getElementById(target)
  if (!card) return scrollToOffers()
  card.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/*
 * The bubble floats at the bottom of the screen while the CV is being read,
 * and goes once its end comes up (the match follows right under it). Fixed,
 * not sticky: the section clips its overflow, which stops sticky positioning.
 */
const cvColumnRef = ref<HTMLElement | null>(null)
const { top: cvTop, bottom: cvBottom } = useElementBounding(cvColumnRef)
const { height: viewportHeight } = useWindowSize()
/** Skipped the posting question and not tailored yet: the bubble asks for the posting instead. */
const bubbleAsksPosting = computed(() => posting.value === '' && !tailorSummary.value)
/** The tailoring is running or on screen. */
const tailorShown = computed(() => !!tailorSummary.value && (tailorSummary.value.running || tailorSummary.value.matchScore !== null))
/** Gave a posting, not tailored to it: the bubble says it can be, and for how much. */
const bubbleOffersPosting = computed(() => !!posting.value && !isPremium.value && (!tailorSummary.value || (!tailorSummary.value.running && tailorSummary.value.matchScore === null)))
const { tailoredPdfPrice } = usePremiumPlans()
const showBubble = computed(() =>
  (bubbleAsksPosting.value
    || bubbleOffersPosting.value
    || (!!tailorSummary.value && (tailorSummary.value.running || tailorSummary.value.matchScore !== null)))
  && cvTop.value < viewportHeight.value
  && cvBottom.value > viewportHeight.value - 80,
)

/*
 * The first question: an old CV or a LinkedIn profile? With one, the file
 * attached in the answer box is read on the server (/api/ai/cv-chat/file), or
 * the text pasted there is taken as it is, and the CV is built from it at
 * once; the result then asks whether to add anything.
 */
const journey = useJourney()
const importFileRef = ref<HTMLInputElement | null>(null)
/** The attached file while it is read, shown as the visitor's message meanwhile. */
const readingFile = ref<string | null>(null)
const importError = ref<string | null>(null)
const isImporting = computed(() => !!readingFile.value || isThinking.value || isBuilding.value)
/** The first question not answered yet: tapped yes or no, typed, or a CV attached or pasted right away. */
const asksOpening = computed(() => !hasStarted.value && opening.value === 'ask')
/** Asked for the old CV and nothing sent yet: the answer box takes it (paperclip or pasted text). */
const awaitsImport = computed(() => !hasStarted.value && opening.value === 'import')
/** Before the first answer the paperclip takes an old CV, whether or not yes was tapped first (and the hint under the box says so). */
const takesCvFile = computed(() => asksOpening.value || awaitsImport.value)
/** Later on it still does: a visitor who first chose the questions can attach the CV after all. */
const canAttachCv = computed(() => takesCvFile.value || !limitReached.value)

async function pickOpening(choice: 'import' | 'interview') {
  importError.value = null
  await chooseOpening(choice)
  nextTick(() => focusesInput() && inputRef.value?.focus())
}

async function submitImport(text: string, how: 'file' | 'paste') {
  importError.value = null
  await importCv(text, how)
  scrollToBottom()
}

const FILE_ERRORS: Record<string, string> = {
  FILE_TYPE: 'cvChat.import.errors.fileType',
  FILE_TOO_LARGE: 'cvChat.import.errors.tooLarge',
  PDF_NO_TEXT: 'cvChat.import.errors.noText',
}

async function onImportFile(event: Event) {
  const field = event.target as HTMLInputElement
  const file = field.files?.[0]
  field.value = ''
  if (!file || isImporting.value) return
  importError.value = null
  readingFile.value = file.name
  scrollToBottom()
  let text = ''
  try {
    const form = new FormData()
    form.append('file', file)
    text = (await $fetch<{ text: string }>('/api/ai/cv-chat/file', { method: 'POST', body: form })).text
  } catch (err) {
    const fetchError = err as { statusCode?: number, data?: { data?: { code?: string } } }
    const code = fetchError.data?.data?.code ?? ''
    importError.value = t(fetchError.statusCode === 429 ? 'cvChat.import.errors.limit' : FILE_ERRORS[code] ?? 'cvChat.import.errors.unreadable')
    journey.track('chat_import_failed', { code: code || fetchError.statusCode || 0 })
  } finally {
    readingFile.value = null
  }
  if (text) await submitImport(text, 'file')
  else scrollToBottom()
}

/** While the CV is built, the panel says what is being written, a step every few seconds. */
const buildSteps = computed(() => (tm('cvChat.buildSteps') as unknown[]).map(step => rt(step as Parameters<typeof rt>[0])))
const buildStep = ref(0)
let buildStepTimer: ReturnType<typeof setInterval> | undefined
watch(isBuilding, (building) => {
  clearInterval(buildStepTimer)
  buildStep.value = 0
  if (building) buildStepTimer = setInterval(() => {
    buildStep.value = Math.min(buildStep.value + 1, buildSteps.value.length - 1)
  }, 2600)
})
onBeforeUnmount(() => clearInterval(buildStepTimer))

function editAnswers() {
  backToChat()
  nextTick(() => {
    rootRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    scrollToBottom()
  })
}

/**
 * The greeting types itself out once, as if the coach were writing it right
 * now: typing dots (already in the server HTML, so the text never flashes in
 * whole first), then the text at an even pace. `null` shows it whole — for a
 * restored interview, with reduced motion or once the visitor answers. The
 * full text is always in the markup for screen readers and crawlers.
 */
const GREETING_DELAY_MS = 600
const GREETING_TYPE_MS = 2200
const typedChars = ref<number | null>(0)
let typingTimer: ReturnType<typeof setTimeout> | undefined
let typingFrame = 0

function stopTyping() {
  clearTimeout(typingTimer)
  cancelAnimationFrame(typingFrame)
  typedChars.value = null
}

function typeGreeting() {
  clearTimeout(typingTimer)
  cancelAnimationFrame(typingFrame)
  if (hasStarted.value || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return stopTyping()

  typedChars.value = 0
  typingTimer = setTimeout(() => {
    const start = performance.now()
    const step = (now: number) => {
      const length = messages.value[0]?.content.length ?? 0
      const chars = Math.ceil(length * Math.min(1, (now - start) / GREETING_TYPE_MS))
      if (hasStarted.value || chars >= length) return stopTyping()
      typedChars.value = chars
      typingFrame = requestAnimationFrame(step)
    }
    typingFrame = requestAnimationFrame(step)
  }, GREETING_DELAY_MS)
}

watch(hasStarted, (started) => {
  if (started) stopTyping()
})

function startOver() {
  reset()
  previewOutput.value = 'pdf'
  props.onEvent?.('chat_restart')
  nextTick(() => rootRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  typeGreeting()
}

// Registered after useCvChat's own onMounted, so a restored interview is already in place
onMounted(() => {
  resultVariant.value = resultExperiment.assign()
  resultTracking.value = resultExperiment.trackingData()
  // The keyboard opening and closing resizes the visible area, and the browser pans it
  if (window.visualViewport) useEventListener(window.visualViewport, ['resize', 'scroll'], pinAboveKeyboard)
  scrollToBottom()
  typeGreeting()
  focusOnArrival()
})
onBeforeUnmount(() => {
  stopTyping()
  document.documentElement.classList.remove('chat-typing')
})
</script>

<template>
  <section ref="rootRef" class="sofax relative overflow-hidden bg-white dark:bg-gray-950">
    <div class="pb-12 pt-3 md:pb-16 md:pt-6 lg:pt-8">
      <div class="shell relative">
        <!-- The headline and the whole chat fit one screen below the sticky
             navbar. Phones: a screen-high column, the headline shrinks to one
             short line without the badge and subtitle, and the message list
             takes the rest, the answer box at the bottom like a messenger.
             Larger screens: the message list grows with the conversation (so
             the answer box starts right under the greeting), scrolls past
             26rem and gives way on short screens, so the answer box is in view
             without scrolling the page either way. -->
        <div ref="columnRef" :class="generatedCV ? '' : 'flex flex-col max-md:h-[calc(100svh-5.5rem)] max-md:min-h-[22rem] md:max-h-[calc(100svh-8.5rem)]'">
          <!-- Homepage: how else the CV can be made. Above the headline so it
               stays put while the modes' headlines change; gone once a CV exists. -->
          <div v-if="$slots.switcher && !generatedCV" class="mb-4 shrink-0 md:mb-6">
            <slot name="switcher" />
          </div>

          <!-- Phones: gone once the CV is there, so the CV itself comes up first -->
          <div v-if="withHeading" class="mx-auto max-w-5xl shrink-0 text-center" :class="generatedCV ? 'max-md:hidden' : ''">
            <h1 class="hero-mode-title">{{ t('cvChat.title') }}</h1>
            <p class="lead mx-auto max-w-3xl max-md:hidden">{{ t('cvChat.subtitle') }}</p>
          </div>

          <!-- The interview: bubbles straight on the hero stage, like a
               messenger; only the answer box is a white card -->
          <!-- Pinned above the phone keyboard while an answer is typed; its
               place on the page stays, empty, under it -->
          <div
            v-if="!generatedCV"
            ref="chatRef"
            class="mx-auto flex min-h-0 w-full max-w-[46rem] flex-col max-md:flex-1 md:flex-initial"
            :class="[
              withHeading ? 'mt-3 md:mt-6' : '',
              pinned && 'fixed inset-x-0 bottom-0 z-[60] !mt-0 bg-white px-4 dark:bg-gray-950',
            ]"
            :style="pinned ? { top: `${pinned.top}px`, paddingBottom: `${pinned.below + KEYBOARD_GAP}px` } : undefined"
          >
            <!-- Messages -->
            <div
              ref="listRef"
              class="chat-list -mr-3 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pb-2 pr-1 pt-3 md:max-h-[26rem] md:flex-initial md:gap-3 md:pt-4"
              aria-live="polite"
            >
              <div
                v-for="(message, index) in messages"
                :key="index"
                class="flex"
                :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
              >
                <!-- A pasted CV scrolls inside its bubble instead of pushing the conversation out of view -->
                <div
                  class="max-w-[88%] whitespace-pre-line break-words rounded-2xl px-3 py-2 text-[0.9375rem] leading-normal md:max-w-[85%] md:px-4 md:py-2.5 md:text-base md:leading-relaxed"
                  :class="message.role === 'user'
                    ? ['rounded-br-md bg-lilac text-ink', message.content.length >= CV_CHAT_PASTED_CV_CHARS && 'max-h-60 overflow-y-auto']
                    : 'rounded-bl-md bg-white/60 text-ink ring-1 ring-black/5 backdrop-blur-sm dark:bg-gray-800/70 dark:text-white dark:ring-white/10'"
                >
                  <span class="sr-only">{{ message.role === 'user' ? t('cvChat.you') : t('cvChat.assistant') }}: </span>
                  <!-- The greeting types itself out; readers get the whole text at once -->
                  <template v-if="index === 0 && typedChars !== null">
                    <span v-if="typedChars === 0" aria-hidden="true"><span class="typing-dot" /><span class="typing-dot" /><span class="typing-dot" /></span>
                    <span v-else aria-hidden="true">{{ message.content.slice(0, typedChars) }}<span class="type-caret" /></span>
                    <span class="sr-only">{{ message.content }}</span>
                  </template>
                  <template v-else>{{ message.content }}</template>
                </div>
              </div>

              <!-- The attached CV while it is read: the visitor's message with the file name -->
              <div v-if="readingFile" class="flex justify-end">
                <div class="inline-flex max-w-[88%] items-center gap-2 rounded-2xl rounded-br-md bg-lilac px-3 py-2 text-[0.9375rem] text-ink md:max-w-[85%] md:px-4 md:py-2.5 md:text-base">
                  <i class="bi bi-file-earmark-text shrink-0" aria-hidden="true" />
                  <span class="truncate">{{ readingFile }}</span>
                  <span class="shrink-0 !text-sm text-ink/70">· {{ t('cvChat.import.reading') }}</span>
                </div>
              </div>

              <!-- Typing indicator -->
              <div v-if="isThinking" class="flex justify-start">
                <div class="rounded-2xl rounded-bl-md bg-white/60 px-3 py-2.5 ring-1 ring-black/5 backdrop-blur-sm md:px-4 md:py-3 dark:bg-gray-800/70 dark:ring-white/10" :aria-label="t('cvChat.thinking')">
                  <span class="typing-dot" /><span class="typing-dot" /><span class="typing-dot" />
                </div>
              </div>

              <!-- The answers to the first question, once it is typed out: quick replies like any other -->
              <div v-if="!hasStarted && !isThinking && !readingFile && typedChars === null && opening !== 'interview'" class="flex flex-wrap justify-end gap-2">
                <template v-if="opening === 'ask'">
                  <button
                    v-for="choice in (['import', 'interview'] as const)"
                    :key="choice"
                    type="button"
                    class="rounded-full border border-lilac bg-lilac/20 px-4 py-1.5 text-sm font-semibold text-ink transition-colors hover:bg-lilac md:text-base dark:text-white dark:hover:text-ink"
                    @mousedown.prevent
                    @click="pickOpening(choice)"
                  >
                    {{ choice === 'import' ? t('cvChat.import.yes') : t('cvChat.import.no') }}
                  </button>
                </template>
                <button
                  v-else-if="messages[messages.length - 1]?.role === 'assistant'"
                  type="button"
                  class="rounded-full border border-lilac bg-lilac/20 px-3 py-1 text-sm font-semibold text-ink transition-colors hover:bg-lilac dark:text-white dark:hover:text-ink"
                  @mousedown.prevent
                  @click="pickOpening('interview')"
                >
                  {{ t('cvChat.import.interview') }}
                </button>
              </div>

              <!-- The old CV is in, asked whether to add anything: nothing, build it now -->
              <div v-if="asksAddAfterImport && !isBuilding" class="flex justify-end">
                <button
                  type="button"
                  class="rounded-full border border-lilac bg-lilac/20 px-4 py-1.5 text-sm font-semibold text-ink transition-colors hover:bg-lilac md:text-base dark:text-white dark:hover:text-ink"
                  @mousedown.prevent
                  @click="buildCv"
                >
                  {{ t('cvChat.import.nothingToAdd') }}
                </button>
              </div>

              <!-- Quick replies the AI proposed for its question: one tap sends,
                   or, when several can be true, they are ticked and sent together.
                   Like the send button, they never take the focus from the answer
                   box (`mousedown.prevent`): on a phone that would close the
                   keyboard and unpin the chat between the touch and the click,
                   and the page would jump to wherever the browser had scrolled it. -->
              <div v-if="suggestions.length && !isThinking && !done" class="flex flex-col items-end gap-2">
                <p v-if="multipleSuggestions" class="!text-sm text-muted dark:text-gray-400">{{ t('cvChat.pickHint') }}</p>
                <div class="flex flex-wrap justify-end gap-2">
                  <template v-if="multipleSuggestions">
                    <button
                      v-for="suggestion in suggestions"
                      :key="suggestion"
                      type="button"
                      :aria-pressed="picked.includes(suggestion)"
                      class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold transition-colors"
                      :class="picked.includes(suggestion)
                        ? 'border-lilac bg-lilac text-ink'
                        : 'border-lilac bg-lilac/20 text-ink hover:bg-lilac/50 dark:text-white dark:hover:text-ink'"
                      @mousedown.prevent
                      @click="togglePick(suggestion)"
                    >
                      <i class="bi text-xs" :class="picked.includes(suggestion) ? 'bi-check-lg' : 'bi-plus-lg'" aria-hidden="true" />
                      {{ suggestion }}
                    </button>
                  </template>
                  <template v-else>
                    <button
                      v-for="suggestion in suggestions"
                      :key="suggestion"
                      type="button"
                      class="rounded-full border border-lilac bg-lilac/20 px-3 py-1 text-sm font-semibold text-ink transition-colors hover:bg-lilac dark:text-white dark:hover:text-ink"
                      @mousedown.prevent
                      @click="submit(suggestion)"
                    >
                      {{ suggestion }}
                    </button>
                  </template>
                </div>
              </div>
            </div>

            <div v-if="error" class="mt-2 flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-[0.625rem] bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              <span><i class="bi bi-exclamation-circle-fill mr-2" />{{ error }}</span>
              <button v-if="lastIsUnanswered && !limitReached" type="button" class="font-semibold underline underline-offset-4" @click="retry">
                {{ t('cvChat.retry') }}
              </button>
              <LocaleLink v-else-if="buildLimitReached" :to="upgradeTarget" class="font-semibold underline underline-offset-4">
                {{ isAuthenticated ? t('cvChat.errors.upgradeMember') : t('cvChat.errors.upgrade') }}
              </LocaleLink>
              <!-- Out of questions: build from what is there first, Premium to keep talking second -->
              <span v-else-if="limitReached" class="flex flex-wrap items-center gap-x-4 gap-y-1">
                <button v-if="canBuild" type="button" class="font-semibold underline underline-offset-4" @click="buildCv">
                  {{ isBuilding ? t('cvChat.building') : t('cvChat.buildNow') }}
                </button>
                <LocaleLink v-if="!limitFairUse && !isPremium" :to="upgradeTarget" class="font-semibold underline underline-offset-4">
                  {{ isAuthenticated ? t('cvChat.errors.upgradeMember') : t('cvChat.errors.upgrade') }}
                </LocaleLink>
              </span>
            </div>

            <p v-if="importError" class="mt-2 shrink-0 rounded-[0.625rem] bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300" role="alert">
              <i class="bi bi-exclamation-circle-fill mr-2" />{{ importError }}
            </p>

            <!-- Building: the answer box gives way to what is being written right now -->
            <div v-if="isBuilding" class="build-panel mt-2 shrink-0 rounded-[1.25rem] border border-line bg-white px-4 py-4 shadow-lift md:mt-3 md:px-5 dark:border-gray-700 dark:bg-gray-800" role="status" aria-live="polite">
              <div class="flex items-center gap-3">
                <span class="build-orb inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-accent/15 text-lg text-accent-strong md:size-12 md:text-xl dark:text-accent-soft" aria-hidden="true">
                  <i class="bi bi-magic" />
                </span>
                <div class="min-w-0 grow">
                  <p class="!text-base font-semibold text-ink dark:text-white">{{ t('cvChat.building') }}</p>
                  <Transition name="build-step" mode="out-in">
                    <p :key="buildStep" class="!text-sm text-muted dark:text-gray-400">{{ buildSteps[buildStep] }}…</p>
                  </Transition>
                </div>
              </div>
              <div class="build-track mt-3 h-1.5 overflow-hidden rounded-full bg-line/60 dark:bg-gray-700" aria-hidden="true">
                <div class="build-bar h-full rounded-full bg-accent-strong" />
              </div>
            </div>

            <!-- Ready: the build takes over the input -->
            <div v-else-if="done" class="mt-3 shrink-0">
              <SofaxBtn
                variant="accent"
                block
                :disabled="!canBuild"
                :label="isBuilding ? t('cvChat.building') : t('cvChat.buildCta')"
                @click="buildCv"
              />
              <p class="mt-3 text-center text-sm text-muted dark:text-gray-400">
                <button type="button" class="font-semibold text-ink underline-offset-4 hover:underline dark:text-white" :disabled="isBuilding" @click="done = false">
                  {{ t('cvChat.addMore') }}
                </button>
              </p>
            </div>

            <!-- Answer box, there from the start. It also takes the old CV: the file
                 (paperclip, all along) or, before the first answer, the pasted text. -->
            <form v-else class="mt-2 shrink-0 md:mt-3" novalidate @submit.prevent="submit()">
              <label for="cv-chat-input" class="sr-only">{{ t('cvChat.inputLabel') }}</label>
              <div class="flex items-end gap-2 rounded-[1.25rem] border border-line bg-white py-1.5 pl-3 pr-1.5 shadow-lift md:py-2 md:pl-4 md:pr-2 transition-colors focus-within:border-accent dark:border-gray-700 dark:bg-gray-800" :class="canAttachCv && '!pl-1.5 md:!pl-2'">
                <template v-if="canAttachCv">
                  <input ref="importFileRef" type="file" class="sr-only" tabindex="-1" accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain" :disabled="isImporting" @change="onImportFile">
                  <button
                    type="button"
                    class="inline-flex size-10 shrink-0 items-center justify-center rounded-full text-xl text-ink transition-colors hover:bg-lilac/40 disabled:opacity-40 md:size-12 dark:text-white"
                    :disabled="isImporting"
                    :aria-label="t('cvChat.import.attach')"
                    :title="t('cvChat.import.attach')"
                    @mousedown.prevent
                    @click="importFileRef?.click()"
                  >
                    <i class="bi bi-paperclip" aria-hidden="true" />
                  </button>
                </template>
                <textarea
                  id="cv-chat-input"
                  ref="inputRef"
                  v-model="input"
                  rows="2"
                  :maxlength="CV_CHAT_MAX_ANSWER"
                  :placeholder="limitReached ? t('cvChat.limitPlaceholder') : awaitsImport ? t('cvChat.import.placeholder') : picked.length ? t('cvChat.pickedPlaceholder') : t('cvChat.placeholder')"
                  :disabled="isBuilding || limitReached || !!readingFile"
                  class="block max-h-40 min-h-[3rem] grow resize-none border-0 bg-transparent px-0 py-1.5 text-base leading-relaxed text-ink placeholder:text-muted focus:outline-none focus:ring-0 disabled:opacity-60 dark:text-white dark:placeholder:text-gray-500"
                  @keydown="onKeydown"
                  @focus="pinAboveKeyboard"
                  @blur="unpin"
                />
                <button
                  type="submit"
                  :disabled="!canSubmit"
                  class="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-accent-strong text-base text-white md:size-12 md:text-lg transition-opacity disabled:opacity-40"
                  :aria-label="t('cvChat.send')"
                  @mousedown.prevent
                >
                  <i class="bi bi-send-fill" />
                </button>
              </div>

              <p v-if="takesCvFile" class="mt-2 px-1 text-center text-sm text-muted md:mt-3 md:text-left dark:text-gray-400">
                <i class="bi bi-paperclip mr-1" aria-hidden="true" />{{ t('cvChat.import.uploadHint') }}
              </p>
              <!-- Phones: the counts on one line, the build centred under them -->
              <div v-else class="mt-2 grid grid-cols-[1fr_auto_1fr] items-center gap-x-3 gap-y-1 px-1 text-sm md:mt-3 md:flex md:flex-wrap md:justify-between md:gap-x-4 md:gap-y-2">
                <!-- Two counts instead of a percentage: answers until the CV can be built, and until it is complete -->
                <span class="col-span-3 text-center text-muted md:text-left dark:text-gray-400">
                  {{ answersLeft > 0 ? t('cvChat.answersLeft', { n: answersLeft }, answersLeft) : t('cvChat.canBuildHint') }}<template v-if="completeLeft > 0"> · {{ t('cvChat.completeLeft', { n: completeLeft }, completeLeft) }}</template>
                </span>
                <div class="contents md:flex md:items-center md:gap-x-4">
                  <button
                    v-if="hasStarted"
                    type="button"
                    class="col-start-1 justify-self-start font-semibold text-muted underline-offset-4 hover:text-ink hover:underline dark:text-gray-400 dark:hover:text-white"
                    :disabled="isThinking || isBuilding"
                    @click="startOver"
                  >
                    {{ t('cvChat.startOver') }}
                  </button>
                  <button
                    type="button"
                    class="col-start-2 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 font-semibold transition-colors"
                    :class="canBuild
                      ? 'build-ready bg-accent-strong text-white hover:bg-ink dark:hover:bg-white dark:hover:text-ink'
                      : 'cursor-not-allowed border border-line text-muted dark:border-gray-700 dark:text-gray-500'"
                    :disabled="!canBuild"
                    @click="buildCv"
                  >
                    <i class="bi bi-magic" aria-hidden="true" />
                    {{ t('cvChat.buildNow') }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <!-- What the interview turns into, while it is still going -->
        <LandingTemplateStrip v-if="!generatedCV && withTemplates" />

        <!-- Result: preview in the paid layouts, and what the CV can become -->
        <div v-if="generatedCV" ref="resultRef" class="mx-auto max-w-6xl scroll-mt-28" :class="withHeading ? 'md:mt-14' : ''">
          <!-- A: the title and how to get the PDF. Phones: the title alone, one short line, and the CV right under it -->
          <div v-if="resultVariant === 'a'" class="mb-3 text-center md:mb-8">
            <h2 class="!text-xl md:!text-4xl">{{ t('cvChat.result.title') }}</h2>
            <p class="mt-2 !text-base max-md:hidden md:mt-3 md:!text-lg">{{ t('cvChat.result.subtitle') }}</p>
          </div>

          <!-- B and C: the win first, then why it is not the end yet -->
          <div v-else class="mb-3 text-center md:mb-6">
            <span class="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-accent-strong max-md:hidden dark:text-accent-soft">
              <i class="bi bi-check-circle-fill" aria-hidden="true" />
              {{ t('cvChat.result.done') }}
            </span>
            <h2 class="!text-xl md:mt-3 md:!text-4xl">{{ t('cvChat.result.title') }}</h2>
            <p class="mx-auto mt-2 max-w-3xl !text-base max-md:hidden md:mt-3 md:!text-lg">
              {{ resultSubtitle }}
            </p>
          </div>

          <!-- From this CV to the interview: what is done, and each next step, which
               is also where its offer is. Phones: one row of four small steps. -->
          <nav v-if="resultVariant === 'b'" :aria-label="t('cvChat.result.path.label')" class="mx-auto mb-4 max-w-4xl md:mb-8">
            <div class="mb-2 flex items-baseline justify-between gap-3 px-1 !text-xs md:!text-sm">
              <span class="font-semibold text-ink dark:text-white">{{ t('cvChat.result.path.label') }}</span>
              <span class="tabular-nums text-muted dark:text-gray-400">{{ t('cvChat.result.path.progress', { n: pathProgress }) }}</span>
            </div>
            <div class="mb-3 h-1.5 overflow-hidden rounded-full bg-line dark:bg-gray-700" aria-hidden="true">
              <div class="h-full rounded-full bg-accent transition-[width] duration-700" :style="{ width: `${pathProgress}%` }" />
            </div>
            <ol class="grid grid-cols-4 gap-1.5 md:gap-3">
              <li v-for="(step, index) in path" :key="step.id">
                <button
                  type="button"
                  class="flex size-full flex-col items-center gap-1 rounded-[0.625rem] border px-1 py-2 text-center transition-colors md:flex-row md:items-start md:gap-3 md:p-3 md:text-left"
                  :class="[
                    step.status === 'done' ? 'border-accent/40 bg-accent/5 dark:bg-accent/10' : step.status === 'next' ? 'border-ink bg-white dark:border-white dark:bg-gray-900' : 'border-line bg-white/70 hover:border-ink dark:border-gray-700 dark:bg-gray-900/60 dark:hover:border-white/60',
                    step.target ? 'cursor-pointer' : 'cursor-default',
                  ]"
                  :disabled="!step.target"
                  :aria-current="step.status === 'next' ? 'step' : undefined"
                  @click="step.target && goToStep(step.target)"
                >
                  <span
                    class="flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-bold md:size-8"
                    :class="step.status === 'done' ? 'bg-accent text-white' : step.status === 'next' ? 'bg-ink !text-white dark:bg-white dark:!text-ink' : 'bg-surface text-muted dark:bg-gray-800 dark:text-gray-400'"
                    aria-hidden="true"
                  >
                    <i v-if="step.status === 'done'" class="bi bi-check-lg" />
                    <template v-else>{{ index + 1 }}</template>
                  </span>
                  <span class="min-w-0">
                    <span class="block !text-[0.6875rem] font-semibold leading-tight text-ink md:!text-sm dark:text-white">{{ t(`cvChat.result.path.steps.${step.id}.title`) }}</span>
                    <span class="mt-0.5 block !text-xs leading-tight max-md:hidden" :class="step.status === 'done' ? 'text-accent-strong dark:text-accent-soft' : 'text-muted dark:text-gray-400'">{{ step.detail }}</span>
                  </span>
                </button>
              </li>
            </ol>
          </nav>

          <div class="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[1.15fr_.85fr] lg:gap-8">
            <div ref="cvColumnRef" class="min-w-0">
              <div
                role="tablist"
                :aria-label="t('landing.builder.result.outputLabel')"
                class="mb-2 flex w-full rounded-full border border-line bg-white p-1 md:mb-3 dark:border-gray-700 dark:bg-gray-800"
              >
                <button
                  v-for="output in outputs"
                  :key="output.id"
                  type="button"
                  role="tab"
                  :aria-selected="previewOutput === output.id"
                  class="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1.5 text-sm font-semibold transition-colors duration-300 md:gap-2 md:px-4 md:py-2 md:text-base"
                  :class="previewOutput === output.id
                    ? 'bg-accent text-white'
                    : 'text-body hover:text-ink dark:text-gray-300 dark:hover:text-white'"
                  @click="previewOutput = output.id"
                >
                  <i class="bi" :class="output.icon" />
                  {{ t(`landing.builder.result.outputs.${output.id}`) }}
                  <!-- The web CV comes with the pack: say so on the tab, not only in the hint under it -->
                  <span
                    v-if="output.id === 'web'"
                    class="rounded-full bg-lilac px-1.5 py-0.5 !text-[0.625rem] font-bold !leading-tight !text-ink"
                  >
                    {{ t('cvChat.templates.premium') }}
                  </span>
                </button>
              </div>

              <LandingTemplatePicker v-if="previewOutput === 'pdf'" v-model="selectedTemplate" />
              <p v-else class="mb-4 text-center !text-sm text-muted dark:text-gray-400">
                {{ t('landing.builder.result.webHint') }}
              </p>

              <!-- Full height, no inner scroll: on a phone the touch lands in the
                   preview's iframe, so a capped box could not be scrolled at all
                   and the end of the CV stayed hidden. The page scrolls instead. -->
              <div
                v-show="previewOutput === 'pdf'"
                class="relative overflow-hidden rounded-[0.625rem] bg-gray-100 dark:bg-gray-900"
              >
                <LandingPreviewDownload v-if="resultOffers" :label="resultOffers.cornerLabel" :busy="resultOffers.cornerBusy" :paid="resultOffers.cornerPaid" @click="resultOffers.cornerDownload()" />
                <LazyCVPreviewWrapper
                  :cv-data="generatedCV"
                  :experiences-list="generatedCV.experiences || []"
                  :education-list="generatedCV.education || []"
                  :show-q-r-code="false"
                  :template="selectedTemplate"
                  :watermark="!isPremium"
                />
              </div>
              <div v-if="previewOutput === 'web'" class="relative">
                <LandingPreviewDownload v-if="resultOffers" :label="resultOffers.websiteLabel" :busy="resultOffers.cornerBusy" paid icon="bi-globe2" @click="resultOffers.cornerWebsite()" />
                <LazyLandingWebCvPreview :cv-data="generatedCV" />
              </div>

              <!-- Phones: the match with the chat's posting floats over the CV
                   while it is read, and one tap takes them to what to improve.
                   Desktop shows the whole match next to the CV instead. -->
              <Transition
                enter-from-class="translate-y-4 opacity-0"
                leave-to-class="translate-y-4 opacity-0"
                enter-active-class="transition duration-300"
                leave-active-class="transition duration-200"
              >
                <div
                  v-if="showBubble && resultVariant !== 'c'"
                  class="pointer-events-none fixed inset-x-4 bottom-4 z-40 flex justify-center lg:hidden"
                >
                  <button
                    type="button"
                    class="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl bg-ink px-4 py-3 text-left text-white shadow-lift dark:bg-white dark:text-ink"
                    @click="scrollToTailor"
                  >
                    <template v-if="bubbleOffersPosting">
                      <span class="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent text-lg text-white" aria-hidden="true">
                        <i class="bi bi-magic" />
                      </span>
                      <span class="min-w-0 grow">
                        <span class="block text-sm font-semibold">{{ t('cvChat.result.bubble.offerTitle') }}</span>
                        <span class="block text-xs opacity-80">{{ t('cvChat.result.bubble.offerText', { price: tailoredPdfPrice }) }}</span>
                      </span>
                      <i class="bi bi-chevron-down shrink-0" aria-hidden="true" />
                    </template>
                    <template v-else-if="bubbleAsksPosting || !tailorSummary">
                      <span class="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent text-lg text-white" aria-hidden="true">
                        <i class="bi bi-magic" />
                      </span>
                      <span class="min-w-0 grow">
                        <span class="block text-sm font-semibold">{{ t('cvChat.result.bubble.askTitle') }}</span>
                        <span class="block text-xs opacity-80">{{ t('cvChat.result.bubble.askText') }}</span>
                      </span>
                      <i class="bi bi-chevron-down shrink-0" aria-hidden="true" />
                    </template>
                    <template v-else-if="tailorSummary.running">
                      <span class="inline-block size-5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
                      <span class="grow text-sm font-semibold">{{ t('cvChat.result.bubble.running') }}</span>
                    </template>
                    <template v-else>
                      <span class="flex size-10 shrink-0 items-center justify-center rounded-full bg-amber-400 font-title text-sm font-bold tabular-nums text-ink">
                        {{ tailorSummary.matchScore }}&nbsp;%
                      </span>
                      <span class="min-w-0 grow">
                        <span class="block text-sm font-semibold">{{ t('cvChat.result.bubble.title', { score: tailorSummary.matchScore }) }}</span>
                        <span class="block text-xs opacity-80">{{ t('cvChat.result.bubble.text', { n: tailorSummary.changes, after: tailorSummary.matchScoreAfter }) }}</span>
                      </span>
                      <i class="bi bi-chevron-down shrink-0" aria-hidden="true" />
                    </template>
                  </button>
                </div>
              </Transition>
            </div>

            <!-- C: the panel stays next to the CV while it is read, as long as it is
                 short enough to: once the tailoring is on it, it runs with the page -->
            <div
              ref="offersRef"
              class="scroll-mt-24 space-y-4 text-left"
              :class="resultVariant === 'c' && !tailorShown && 'lg:sticky lg:top-24 lg:self-start'"
            >
              <LazyLandingResultOffers
                ref="resultOffers"
                :cv-data="generatedCV"
                :template="selectedTemplate"
                :generation-id="generationId"
                :signup-source="source"
                :cv-id="cvId"
                :initial-posting="posting || null"
                :layout="RESULT_LAYOUT[resultVariant]"
                :tracking-data="resultTracking"
                consent-source="cv-chat"
                job-match
                @tailor="tailorSummary = $event"
                @pick-template="selectedTemplate = $event"
              >
                <template #links>
                  <button type="button" class="link" @click="editAnswers">
                    {{ t('cvChat.result.editAnswers') }}
                  </button>
                  <button type="button" class="link" @click="startOver">
                    {{ t('cvChat.startOver') }}
                  </button>
                </template>
              </LazyLandingResultOffers>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- While the CV is being built: which job is it for? -->
    <LandingPostingDialog :dialog="postingDialog" :building="isBuilding" />
  </section>
</template>

<style scoped>
.chat-list {
  overscroll-behavior: contain;

  /* Without a card around it, older messages fade out at the top edge */
  mask-image: linear-gradient(to bottom, transparent 0, #000 1rem);

  /* A thin messenger-style scrollbar with no track, shown while the pointer
     is over the conversation. The gutter is kept (and pushed out by -mr-3)
     so the bubbles line up with the answer box and never shift. */
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  transition: scrollbar-color 0.2s;
}

.chat-list:hover,
.chat-list:focus-within {
  scrollbar-color: rgb(14 14 14 / 0.18) transparent;
}

:global(.dark-mode) .chat-list:hover,
:global(.dark-mode) .chat-list:focus-within {
  scrollbar-color: rgb(255 255 255 / 0.22) transparent;
}

/* Safari has no scrollbar-color; Chrome and Firefox ignore these once it is set */
.chat-list::-webkit-scrollbar {
  width: 6px;
}

.chat-list::-webkit-scrollbar-track {
  background: transparent;
}

.chat-list::-webkit-scrollbar-thumb {
  border-radius: 9999px;
  background: transparent;
}

.chat-list:hover::-webkit-scrollbar-thumb {
  background: rgb(14 14 14 / 0.18);
}

.type-caret {
  display: inline-block;
  width: 2px;
  height: 1.1em;
  margin-left: 1px;
  vertical-align: text-bottom;
  background: currentColor;
  animation: caret 0.8s steps(1) infinite;
}

@keyframes caret {
  50% { opacity: 0; }
}

.typing-dot {
  display: inline-block;
  width: 0.45rem;
  height: 0.45rem;
  margin: 0 0.12rem;
  border-radius: 9999px;
  background: currentColor;
  opacity: 0.35;
  animation: typing 1.2s infinite ease-in-out;
}

.typing-dot:nth-child(2) {
  animation-delay: 0.15s;
}

.typing-dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes typing {
  0%, 80%, 100% { opacity: 0.35; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
}

/* Building: a bar that keeps creeping forward (the build has no real progress
   to report) with a sheen running over it, and a softly breathing icon */
.build-bar {
  width: 92%;
  transform-origin: left;
  background-image: linear-gradient(90deg, transparent 0, rgb(255 255 255 / 0.45) 50%, transparent 100%);
  background-size: 40% 100%;
  background-repeat: no-repeat;
  animation: build-progress 14s cubic-bezier(0.1, 0.6, 0.3, 1) forwards, build-sheen 1.4s linear infinite;
}

@keyframes build-progress {
  from { transform: scaleX(0.04); }
  to { transform: scaleX(1); }
}

@keyframes build-sheen {
  from { background-position: -40% 0; }
  to { background-position: 140% 0; }
}

.build-orb {
  animation: build-breathe 1.8s ease-in-out infinite;
}

@keyframes build-breathe {
  0%, 100% { box-shadow: 0 0 0 0 rgb(12 147 71 / 0.35); }
  50% { box-shadow: 0 0 0 8px rgb(12 147 71 / 0); }
}

.build-ready {
  box-shadow: 0 6px 18px -6px rgb(12 147 71 / 0.6);
}

.build-step-enter-active,
.build-step-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.build-step-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

.build-step-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .typing-dot,
  .build-orb {
    animation: none;
  }

  .build-bar {
    animation: build-progress 14s linear forwards;
    background-image: none;
  }
}
</style>
